import React, { useEffect, useState } from 'react';
import { Button, Modal, Form, Input, Select, message, Tag, Space, Popconfirm } from 'antd';
import ResponsiveTable from '../components/ResponsiveTable';
import { PlusOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import { ticketsApi, employeesApi, dictionariesApi } from '../services/api-services';

const { TextArea } = Input;

const TicketList: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [evaluateModalVisible, setEvaluateModalVisible] = useState(false);
  const [currentTicket, setCurrentTicket] = useState<any>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<{ label: string; value: string }[]>([]);
  const [form] = Form.useForm();
  const [evaluateForm] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchData = async (status?: string, page = currentPage, size = pageSize) => {
    setLoading(true);
    try {
      const res: any = await ticketsApi.list({ status_filter: status, page, page_size: size });
      setData(res.items);
      setTotal(res.total);
    } catch (e) { message.error('获取工单列表失败'); }
    setLoading(false);
  };

  useEffect(() => { setCurrentPage(1); fetchData(statusFilter, 1, pageSize); }, [statusFilter]);
  useEffect(() => {
    employeesApi.list().then((res: any) => setEmployees(res)).catch(() => {});
    dictionariesApi.getByCode('order_status').then((dict: any) => {
      if (dict?.items?.length) {
        setStatusOptions(dict.items.map((i: any) => ({ label: i.name, value: String(i.value) })));
      }
    }).catch(() => {});
    dictionariesApi.getByCode('ticket_category').then((dict: any) => {
      if (dict?.items?.length) {
        setCategoryOptions(dict.items.map((i: any) => ({ label: i.name, value: String(i.value) })));
      }
    }).catch(() => {});
  }, []);

  const handleSave = async (values: any) => {
    try {
      await ticketsApi.create(values);
      message.success('创建成功');
      setModalVisible(false);
      form.resetFields();
      fetchData(statusFilter);
    } catch (e: any) { message.error(e.response?.data?.detail || '创建失败'); }
  };

  const handleDelete = async (id: number) => {
    try {
      await ticketsApi.delete(id);
      message.success('删除成功');
      fetchData(statusFilter);
    } catch (e) { message.error('删除失败'); }
  };

  const handleCancel = async (id: number) => {
    try {
      await ticketsApi.cancel(id);
      message.success('取消成功');
      fetchData(statusFilter);
    } catch (e: any) { message.error(e.response?.data?.detail || '取消失败'); }
  };

  const handleEvaluate = async (values: any) => {
    if (!currentTicket) return;
    try {
      await ticketsApi.evaluate(currentTicket.id, values.evaluation);
      message.success('评估成功');
      setEvaluateModalVisible(false);
      evaluateForm.resetFields();
      setCurrentTicket(null);
      fetchData(statusFilter);
    } catch (e: any) { message.error(e.response?.data?.detail || '评估失败'); }
  };

  const openEvaluateModal = (record: any) => {
    setCurrentTicket(record);
    evaluateForm.resetFields();
    setEvaluateModalVisible(true);
  };

  const handleUpdateStatus = async (status: string) => {
    if (!currentTicket) return;
    try {
      await ticketsApi.update(currentTicket.id, { status });
      setCurrentTicket({ ...currentTicket, status });
      message.success('状态已更新');
      fetchData(statusFilter);
    } catch (e: any) { message.error(e.response?.data?.detail || '更新失败'); }
  };

  const openDetailModal = async (record: any) => {
    try {
      const res: any = await ticketsApi.get(record.id);
      setCurrentTicket(res);
      setDetailModalVisible(true);
    } catch (e) { message.error('获取工单详情失败'); }
  };

  const columns = [
    { title: '任务ID', dataIndex: 'task_id', key: 'task_id', width: 140, ellipsis: true },
    {
      title: '问题描述', dataIndex: 'problem_description', key: 'problem_description', width: 150,
      render: (v: string) => (
        <span title={v} style={{ display: 'inline-block', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>{v}</span>
      ),
    },
    { title: '分类', dataIndex: 'category', key: 'category', width: 100, align: 'center' as const },
    {
      title: '数字人', dataIndex: 'digital_employee_names', key: 'digital_employee_names', width: 120, align: 'center' as const,
      render: (v: string[]) => v?.length ? v.map((n, i) => <Tag key={i} color="blue" style={{ margin: '2px 2px 2px 0' }}>{n}</Tag>) : '-',
    },
    {
      title: '指派专家', dataIndex: 'expert_name', key: 'expert_name', width: 100, align: 'center' as const,
      render: (v: string) => v ? <Tag color="purple" style={{ margin: '2px 0' }}>{v}</Tag> : '-',
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100, align: 'center' as const,
      render: (status: string) => {
        const opt = statusOptions.find((o) => o.value === String(status));
        return <Tag style={{ margin: '2px 4px 2px 0' }}>{opt?.label ?? status}</Tag>;
      },
    },
    {
      title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v ? new Date(v).toLocaleString() : '-'}</span>,
    },
    {
      title: '操作', key: 'action', width: 120, fixed: 'right' as const, align: 'center' as const,
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetailModal(record)}>详情</Button>
          {(record.status === 'pending_evaluation') && (
            <Button type="link" size="small" icon={<CheckOutlined />} onClick={() => openEvaluateModal(record)}>评估</Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">工单管理</h1>
      </div>
      <p className="apple-page-subtitle">跟踪和管理任务工单，监控处理进度和评估结果。</p>
      <div className="apple-toolbar">
        <Select
          allowClear
          placeholder="状态筛选"
          className="apple-toolbar-select"
          value={statusFilter}
          onChange={(val) => setStatusFilter(val)}
          options={statusOptions}
        />
        <Button className="apple-btn-create" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalVisible(true); }}>
          新建工单
        </Button>
      </div>

      <ResponsiveTable columns={columns} dataSource={data} rowKey="id" loading={loading} pagination={{ current: currentPage, pageSize: pageSize, total: total, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (t) => `共 ${t} 条`, onChange: (page, size) => { setCurrentPage(page); setPageSize(size); fetchData(statusFilter, page, size); } }} />

      {/* Create Modal */}
      <Modal className="apple-modal" title="新建工单" open={modalVisible} onCancel={() => setModalVisible(false)} onOk={() => form.submit()} width={600}>
        <Form className="apple-form" form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="task_id" label="任务ID" rules={[{ required: true, message: '请输入任务ID' }]}>
            <Input placeholder="请输入任务ID" />
          </Form.Item>
          <Form.Item name="problem_description" label="问题描述" rules={[{ required: true, message: '请输入问题描述' }]}>
            <TextArea rows={4} placeholder="请输入问题描述" />
          </Form.Item>
          <Form.Item name="category" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
            <Select placeholder="请选择分类" options={categoryOptions} />
          </Form.Item>
          <Form.Item name="digital_employee_ids" label="数字人">
            <Select
              mode="multiple"
              placeholder="选择数字人"
              options={employees.map((e: any) => ({ label: `${e.name} (${e.identifier})`, value: e.id }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        className="apple-modal"
        title="工单详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={
          <Space>
            {currentTicket && (currentTicket.status === 'processing' || currentTicket.status === 'pending_evaluation') && (
              <Popconfirm title="确定取消此工单?" onConfirm={() => { handleCancel(currentTicket.id); setDetailModalVisible(false); }}>
                <Button danger icon={<CloseOutlined />}>取消工单</Button>
              </Popconfirm>
            )}
            {currentTicket && (
              <Popconfirm title="确定删除此工单?" onConfirm={() => { handleDelete(currentTicket.id); setDetailModalVisible(false); }}>
                <Button danger icon={<DeleteOutlined />}>删除</Button>
              </Popconfirm>
            )}
          </Space>
        }
        width={800}
      >
      {currentTicket && (
          <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
            {(() => {
              const labelMap: Record<string, string> = {
                id: 'ID', task_id: '任务ID', problem_description: '问题描述',
                category: '分类',
                digital_employee_ids: '数字人IDs', digital_employee_names: '数字人',
                expert_id: '专家ID', expert_name: '指派专家',
                status: '状态', final_report: '最终报告',
                user_comment: '用户评价', expert_opinion: '专家意见',
                handover_doc: '交接文档', created_at: '创建时间', ended_at: '结束时间',
              };
              const skipKeys = new Set(['id', 'digital_employee_ids', 'expert_id', 'category', 'digital_employee_names', 'expert_name']);
              return (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {Object.entries(currentTicket).map(([key, value]) => {
                      if (skipKeys.has(key)) return null;
                      if (key === 'expert_name') {
                        return (
                          <tr key={key} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600, width: 140, color: 'var(--apple-text-secondary)' }}>{labelMap[key] || key}</td>
                            <td style={{ padding: '8px 12px' }}>{value ? <Tag color="purple">{value as string}</Tag> : '-'}</td>
                          </tr>
                        );
                      }
                      if (key === 'status') {
                        const opt = statusOptions.find((o) => o.value === String(currentTicket.status));
                        const TERMINAL = new Set(['completed', 'cancelled', 'closed', 'aborted']);
                        const isTerminal = TERMINAL.has(String(currentTicket.status));
                        return (
                          <tr key={key} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600, width: 140, color: 'var(--apple-text-secondary)' }}>{labelMap[key] || key}</td>
                            <td style={{ padding: '8px 12px' }}>
                              {isTerminal
                                ? <Tag>{opt?.label ?? String(currentTicket.status)}</Tag>
                                : <Select size="small" value={String(currentTicket.status)} options={statusOptions} onChange={handleUpdateStatus} style={{ minWidth: 120 }} />
                              }
                            </td>
                          </tr>
                        );
                      }
                      if (key === 'digital_employee_names') {
                        const names = (value as string[]) || [];
                        return (
                          <tr key={key} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600, width: 140, color: 'var(--apple-text-secondary)' }}>{labelMap[key] || key}</td>
                            <td style={{ padding: '8px 12px' }}>{names.length ? names.map((n, i) => <Tag key={i} color="blue" style={{ margin: '2px 2px 2px 0' }}>{n}</Tag>) : '-'}</td>
                          </tr>
                        );
                      }
                      if (key === 'created_at' || key === 'ended_at') {
                        return (
                          <tr key={key} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600, width: 140, color: 'var(--apple-text-secondary)' }}>{labelMap[key] || key}</td>
                            <td style={{ padding: '8px 12px' }}>{value ? new Date(value as string).toLocaleString() : '-'}</td>
                          </tr>
                        );
                      }
                      if (typeof value === 'object' && value !== null) return null;
                      return (
                        <tr key={key} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                          <td style={{ padding: '8px 12px', fontWeight: 600, width: 140, color: 'var(--apple-text-secondary)' }}>{labelMap[key] || key}</td>
                          <td style={{ padding: '8px 12px' }}>{(value as string) || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}
          </div>
        )}
      </Modal>

      {/* Evaluate Modal */}
      <Modal
        className="apple-modal"
        title="评估工单"
        open={evaluateModalVisible}
        onCancel={() => { setEvaluateModalVisible(false); setCurrentTicket(null); }}
        onOk={() => evaluateForm.submit()}
        width={500}
      >
        {currentTicket && (
          <div style={{ marginBottom: 16 }}>
            <p><strong>任务ID:</strong> {currentTicket.task_id}</p>
            <p><strong>问题描述:</strong> {currentTicket.problem_description}</p>
          </div>
        )}
        <Form className="apple-form" form={evaluateForm} layout="vertical" onFinish={handleEvaluate}>
          <Form.Item name="evaluation" label="评估结果" rules={[{ required: true, message: '请选择评估结果' }]}>
            <Select placeholder="请选择评估结果">
              <Select.Option value="approved">
                <CheckOutlined /> 通过 (approved)
              </Select.Option>
              <Select.Option value="rejected">
                <CloseOutlined /> 拒绝 (rejected)
              </Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TicketList;
